import { useEffect, useRef, useState } from 'react';

interface UseScrollRevealOptions {
  threshold?: number;
  rootMargin?: string;
  triggerOnce?: boolean;
}

export const useScrollReveal = (options: UseScrollRevealOptions = {}) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const { threshold = 0.1, rootMargin = '0px 0px -50px 0px', triggerOnce = true } = options;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (triggerOnce && ref.current) {
            observer.unobserve(ref.current);
          }
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      { threshold, rootMargin }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [threshold, rootMargin, triggerOnce]);

  return { ref, isVisible };
};

// Composant wrapper pour les révélations
interface ScrollRevealProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  direction?: 'up' | 'down' | 'left' | 'right' | 'fade';
}

export const ScrollReveal = ({ 
  children, 
  className = '', 
  delay = 0, 
  direction = 'up' 
}: ScrollRevealProps) => {
  const { ref, isVisible } = useScrollReveal();

  const getTransformClass = () => {
    if (isVisible) return 'opacity-100 translate-x-0 translate-y-0 scale-100';
    
    switch (direction) {
      case 'up':
        return 'opacity-0 translate-y-8 scale-95';
      case 'down':
        return 'opacity-0 -translate-y-8 scale-95';
      case 'left':
        return 'opacity-0 translate-x-8 scale-95';
      case 'right':
        return 'opacity-0 -translate-x-8 scale-95';
      case 'fade':
        return 'opacity-0 scale-95';
      default:
        return 'opacity-0 translate-y-8 scale-95';
    }
  };

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out h-full ${getTransformClass()} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};